// Shared text-search primitives: diacritic-insensitive normalization plus a
// substring/subsequence fuzzy matcher. Used by the SelectInput dropdown, the
// onboarding wizard and the public researcher directory so search behaves the
// same everywhere.

const WHITESPACE_RE = /\s+/g;
// Combining diacritical marks (U+0300–U+036F). Built with fromCharCode so no raw
// combining characters are embedded in the source.
const DIACRITIC_RE = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g');
const NON_SEARCH_CHAR_RE = /[^a-z0-9\s]/g;

/** Lowercase and strip diacritics, keeping spaces. For plain substring matching. */
export const normalizeForSearch = (v: string) =>
  v.toLowerCase().normalize('NFD').replace(DIACRITIC_RE, '');

const normalizeSearchText = (v: string) =>
  normalizeForSearch(v).replace(NON_SEARCH_CHAR_RE, ' ').replace(WHITESPACE_RE, ' ').trim();

/** Collapse to a diacritic-free, symbol-free, space-free token for fuzzy compare. */
export const compactSearchText = (v: string) => normalizeSearchText(v).replace(WHITESPACE_RE, '');

function isSubsequence(query: string, haystack: string): boolean {
  let queryIndex = 0;
  for (const char of haystack) {
    if (char === query[queryIndex]) queryIndex += 1;
    if (queryIndex === query.length) return true;
  }
  return false;
}

/**
 * Fuzzy match: the compacted query must appear as a substring or an ordered
 * subsequence of the compacted concatenation of the candidate values.
 * An empty query matches everything.
 */
export function matchesNormalizedFuzzySearch(query: string, values: string[]): boolean {
  const normalizedQuery = compactSearchText(query);
  if (!normalizedQuery) return true;
  const haystack = values.map(compactSearchText).join('');
  return haystack.includes(normalizedQuery) || isSubsequence(normalizedQuery, haystack);
}

/**
 * Relevance score for ranking fuzzy matches. Same match set as
 * `matchesNormalizedFuzzySearch` (any score > 0 is a match), but higher scores
 * mean a tighter match so callers can sort best-first. An empty query returns 0
 * (neutral — callers should keep their natural order).
 *
 * Tiers, best to worst, taking the strongest-matching field:
 *   100 exact field · 90 exact word (e.g. a surname that IS the query)
 *    75 word prefix · 70 field prefix · 50 substring · 20 subsequence only
 *
 * `allowSubsequence` exists for callers that search over LONG fields. The
 * subsequence tier only asks that the query's letters appear in order, which is
 * forgiving on a name and useless on a paragraph: against any long enough text
 * almost every short query matches, so the result set stops discriminating. Pass
 * `false` for descriptive fields (unit, faculty, research line) and keep the
 * default for names.
 */
export function scoreNormalizedFuzzySearch(
  query: string,
  values: string[],
  allowSubsequence = true,
): number {
  const compactQuery = compactSearchText(query);
  if (!compactQuery) return 0;

  let best = 0;
  for (const raw of values) {
    const normalized = normalizeSearchText(raw); // spaces kept between words
    const compact = normalized.replace(WHITESPACE_RE, '');
    if (!compact) continue;

    const tokens = normalized.split(' ').filter(Boolean);
    let score = 0;
    if (compact === compactQuery) score = 100;
    else if (tokens.some((t) => t === compactQuery)) score = 90;
    else if (tokens.some((t) => t.startsWith(compactQuery))) score = 75;
    else if (compact.startsWith(compactQuery)) score = 70;
    else if (compact.includes(compactQuery)) score = 50;
    else if (allowSubsequence && isSubsequence(compactQuery, compact)) score = 20;

    if (score > best) best = score;
  }
  return best;
}
